import { LocalDate } from "@js-joda/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useTipsComputedData } from "./hooks/tips.computedData.hook";
import { useTipCrudActions } from "./hooks/tips.crudActions.hook";
import { useTipDistributionActions } from "./hooks/tips.distributionActions.hook";
import { useTipLoader } from "./hooks/tips.loader.hook";
import { useTipPayoutActions } from "./hooks/tips.payoutActions.hook";
import type { TipsSummaryFilter } from "./logic/tips.v2.logic";
import type { TipDetails } from "model/Tip";
import type { TipsTab, TipsViewModel } from "./tips.component.types";

export type { TipsTab, TipsViewModel } from "./tips.component.types";

export const useTipsViewModel = (): TipsViewModel => {
  const { i18n, t } = useTranslation();
  const [companyState] = useCompanyStore();
  const companyId = companyState.company?.id ?? null;
  const [activeTab, setActiveTab] = useState<TipsTab>("tips");
  const [activeSummaryFilter, setActiveSummaryFilter] =
    useState<TipsSummaryFilter>("all");
  const [currentDate, setCurrentDate] = useState<LocalDate>(() =>
    LocalDate.now(),
  );
  const [selectedDates, setSelectedDates] = useState<readonly LocalDate[]>([]);
  const [tips, setTips] = useState<readonly TipDetails[]>([]);
  const [isLoadingTips, setIsLoadingTips] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useTipLoader({
    companyId,
    currentDate,
    setErrorMessage,
    setIsLoadingTips,
    setTips,
    t,
  });

  const computedData = useTipsComputedData({
    activeSummaryFilter,
    activeTab,
    currentDate,
    i18nLanguage: i18n.language,
    t,
    tips,
  });
  const crudActions = useTipCrudActions({
    companyId,
    setCurrentDate,
    setErrorMessage,
    setIsLoadingTips,
    setSelectedDates,
    setTips,
    t,
  });
  const distributionActions = useTipDistributionActions({
    companyId,
    setErrorMessage,
    setTips,
    t,
    tips,
  });
  const payoutActions = useTipPayoutActions({
    companyId,
    setErrorMessage,
    setTips,
    t,
    tips,
  });

  return {
    ...computedData,
    ...crudActions,
    ...distributionActions,
    ...payoutActions,
    activeSummaryFilter,
    activeTab,
    companyId,
    currentDate,
    errorMessage,
    isLoadingTips,
    selectedDates,
    setActiveSummaryFilter,
    setActiveTab,
    setCurrentDate,
    tips,
  };
};
